import { Button } from '@thanx/uikit/button'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { useDebouncedCallback } from 'use-debounce'

const t = buildTranslate('components.table_pagination')

// 2px belize ring for keyboard focus (WCAG 2.4.7) — see stackedPrevNextOverrides.
const FOCUS_RING_SHADOW = `0 0 0 2px ${palette.belizeHole}`

// baseui drives its own style fn (and focus ring) off `$isFocusVisible`, not
// a `:focus-visible` CSS pseudo-class — see baseui/button/styled-components.js.
type BaseButtonStyleProps = { $isFocusVisible: boolean }

// Shared across the feedback surfaces (LocationsTab uses [10,25,50,100] already;
// nexus caps per_page at 1..100). The mock's `5` reads as demo data for a 6-row
// fixture — adding it later is a one-element change.
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const
export type PageSize = typeof PAGE_SIZE_OPTIONS[number]
export const DEFAULT_PAGE_SIZE: PageSize = 25

const PAGE_INPUT_WIDTH_PX = 80
const PAGE_INPUT_HEIGHT_PX = 48
const SELECT_WIDTH_PX = 120
const SELECT_HEIGHT_PX = 48
// Gap between the three zones of the center pagination group ([Prev] [Page] [Next]).
const PAGINATION_GROUP_GAP_PX = 16
// Gap between a Prev/Next chevron and its label.
const PREV_NEXT_GAP_PX = 4
// Gap between "Page" label and the input, and between the input and "of N".
const PAGE_GROUP_GAP_PX = 12
// Gap between the "Rows per page" label and the select.
const ROWS_PER_PAGE_GAP_PX = 14
// Darshan's "sweet spot" for the page input; an approximate value is fine.
const PAGE_COMMIT_DEBOUNCE_MS = 700

// `layout="stacked"` sizes below, cited against prototype v69
// (~/veda/projects/unified-feedback-inbox/prototype/v69/index.html) CSS
// `.ib-pagi*` (lines 1175-1184) and markup `#ibPagi` (lines 1901-1919). Bumped
// the prototype's 12.5px baseline up to this repo's next real step, 13px.
const STACKED_FONT_SIZE_PX = 13
const STACKED_CONTAINER_PADDING = '10px 16px' // .ib-list-foot { padding }
const STACKED_ROW_GAP_PX = 9 // .ib-pagi { gap }
const STACKED_PAGINATION_ROW_GAP_PX = 8 // .ib-pagi-l { gap }
const STACKED_PAGE_GROUP_GAP_PX = 7 // .ib-pagi-mid { gap }
const STACKED_PREV_NEXT_GAP_PX = 3 // .ib-pagi-btn { gap }
const STACKED_ROWS_PER_PAGE_GAP_PX = 7 // .ib-pagi-r { gap }
const STACKED_BUTTON_PADDING = '4px 2px' // .ib-pagi-btn { padding }
const STACKED_INPUT_PADDING = '4px 2px' // .ib-pagi-input { padding }
const STACKED_PAGE_INPUT_WIDTH_PX = 34 // .ib-pagi-input { width }
const STACKED_BORDER_RADIUS_PX = 6 // .ib-pagi-input / .ib-pagi-size { border-radius }
// The prototype's select has no custom caret (native OS arrow), so its
// `5px 7px` padding is widened on the right here to leave room for the
// reused caret glyph (see the `backgroundImage` below).
const STACKED_SELECT_PADDING = '5px 20px 5px 7px'

type Layout = 'row' | 'stacked'

// uikit's Button bakes its `size`-based padding/fontSize into
// `overrides.BaseButton.style` (@thanx/uikit/button/style.js `getSizeStyle`),
// which — like any styletron-generated class — wins ties against a plain
// `className` padding override by injection order. `overrides.BaseButton`
// is the only way to actually change it, but passing a function does NOT
// replace baseui's own default BaseButton style wholesale — styletron-react
// deep-merges the two (see `$style` handling in
// styletron-react/dist/index.js), so a plain top-level `backgroundColor`
// here still leaves baseui's own `:disabled` (grey `buttonDisabledFill`) and,
// since no `kind` prop reaches the underlying baseui Button (see
// @thanx/uikit's Button.js — `kind`/`size` are consumed by the wrapper, not
// forwarded), its primary-kind `:hover` background intact underneath. Those
// exact pseudo-selector keys are re-declared below so Prev/Next stay a bare
// text button in every state (v69 `.ib-pagi-btn`) — hover/active color
// transitions are otherwise intentionally dropped for this compact variant.
//
// baseui's focus ring lives in the same default style object we're merging
// against — re-supplied here (a11y). Exported so the focus-ring composition
// is unit-testable (see index.test.tsx) — styletron's css() is a no-op in
// the default jsdom test env, so the value passed to `overrides` is the only
// thing a style-object-only suite can assert on.
export function stackedPrevNextOverrides(disabled: boolean) {
  return {
    BaseButton: {
      style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
        backgroundColor: 'transparent',
        border: 'none',
        padding: STACKED_BUTTON_PADDING,
        fontSize: `${STACKED_FONT_SIZE_PX}px`,
        color: disabled ? palette.grey40 : palette.belizeHole,
        boxShadow: $isFocusVisible ? FOCUS_RING_SHADOW : 'none',
        ':hover': {
          backgroundColor: 'transparent',
        },
        ':disabled': {
          backgroundColor: 'transparent',
          color: palette.grey40,
        },
      }),
    },
  }
}

type Props = {
  page: number
  pageSize: PageSize
  totalCount: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: PageSize) => void
  // 'row' (default) is the original single-row layout used by full-width
  // tables. 'stacked' renders the same controls across three compact rows
  // for the 300px-wide feedback inbox list pane (see the prototype cited
  // above) — everything except sizing/arrangement is shared between them.
  layout?: Layout
}

// `layout="row"` (default) is the original single flex row, sized for
// full-width tables (RecoveryAI Activity/RestrictedCustomers, the Order
// Feedback report's DetailsPane); it is unchanged by the addition below.
// `layout="stacked"` exists because that single row wraps garbled inside the
// 300px-wide FeedbackInbox list pane ("of 1" breaking mid-line, "Next" pushed
// off, a horizontal scrollbar) — it re-renders the same state/handlers across
// three compact rows instead of trying to shrink the row layout to fit.
const TablePaginationFooter = ({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
  layout = 'row',
}: Props): React.ReactElement | null => {
  const [css] = useStyletron()

  // Local draft mirrors what the user is typing so the number input can be
  // transiently empty/invalid without the controlled value snapping it back
  // mid-keystroke (harvested from EscalationsQueue/Pagination). Re-sync when the
  // canonical page changes; blur flushes a valid pending entry immediately
  // (so tabbing/clicking away doesn't discard it) and snaps an invalid one back.
  const [draft, setDraft] = useState(String(page))
  useEffect(() => {
    setDraft(String(page))
  }, [page])

  // Commit the typed page on a debounce so typing "12" fires ONE navigation (page 12),
  // not an intermediate page-1 fetch per keystroke. setDraft stays synchronous below
  // so the input never snaps back mid-type; blur/Enter flush immediately.
  const [commitPage, cancelCommitPage, flushCommitPage] = useDebouncedCallback(
    (raw: string) => {
      const parsed = parseInt(raw, 10)
      if (isNaN(parsed)) return
      const clamped = Math.min(Math.max(parsed, 1), totalPages)
      onPageChange(clamped)
      // Snap the draft to the clamped value: when the clamp is a no-op (already on the
      // last page, user types a higher number) `page` doesn't change, so the [page]
      // re-sync effect won't fire and the input would otherwise show the raw entry.
      setDraft(String(clamped))
    },
    PAGE_COMMIT_DEBOUNCE_MS
  )

  // Drop a pending commit on unmount so we don't navigate after teardown.
  useEffect(() => cancelCommitPage, [cancelCommitPage])

  // Hooks must run unconditionally, so the zero-state early return sits after them.
  // Consumers render their own empty state (components/NoSearchResultsFound).
  if (totalCount === 0) return null

  const isFirst = page <= 1
  const isLast = page >= totalPages
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, totalCount)

  const handleDraftChange = (raw: string) => {
    setDraft(raw)
    commitPage(raw)
  }

  if (layout === 'stacked') {
    return (
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: `${STACKED_ROW_GAP_PX}px`,
          borderTop: `1px solid ${palette.grey20}`,
          padding: STACKED_CONTAINER_PADDING,
          boxSizing: 'border-box',
        })}
      >
        {/* Row 1: results summary. The numbers are bolded via a <b> tag baked
            into the locale string and rendered with dangerouslySetInnerHTML —
            the same buildTranslate + dangerouslySetInnerHTML idiom as
            RecoveryAI/Activation/ActivationAgreement.tsx, whose load-bearing
            step is escape()-ing the interpolated value (react-i18nify does
            not escape interpolations). start/end/totalCount here are always
            numbers, not merchant-controlled strings, so no escaping is
            needed — if a string interpolation is ever added to this locale
            key, it must go through escape() the same way. The bold numbers
            render in the same grey70 as the rest of the line (no established
            precedent for baking a palette color into a translated HTML
            string without hardcoding its hex). */}
        <span
          className={css({
            fontSize: `${STACKED_FONT_SIZE_PX}px`,
            color: palette.grey70,
          })}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('showing_results_stacked', {
              start,
              end,
              count: totalCount,
            }),
          }}
        />

        {/* Row 2: Prev / Page input / Next. */}
        <div
          className={`d-flex align-items-center justify-content-between ${css({
            gap: `${STACKED_PAGINATION_ROW_GAP_PX}px`,
          })}`}
        >
          <Button
            kind="minimal"
            disabled={isFirst}
            onClick={() => onPageChange(page - 1)}
            overrides={stackedPrevNextOverrides(isFirst)}
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: `${STACKED_PREV_NEXT_GAP_PX}px`,
            })}
          >
            <i className="fa fa-chevron-left" aria-hidden="true" />
            <span className={css({ fontWeight: 800 })}>{t('prev')}</span>
          </Button>

          <div
            className={`d-flex align-items-center ${css({
              gap: `${STACKED_PAGE_GROUP_GAP_PX}px`,
            })}`}
          >
            <span
              className={css({
                fontSize: `${STACKED_FONT_SIZE_PX}px`,
                color: palette.grey70,
              })}
            >
              {t('page')}
            </span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={draft}
              onChange={e => handleDraftChange(e.target.value)}
              onKeyDown={e => {
                // Enter commits immediately rather than waiting out the debounce.
                if (e.key === 'Enter') flushCommitPage()
              }}
              onBlur={() => {
                // A valid draft still has a pending debounced commit at blur time;
                // flush it so navigating away doesn't discard it. An invalid/empty
                // draft has nothing worth committing, so cancel and snap back.
                if (isNaN(parseInt(draft, 10))) {
                  cancelCommitPage()
                  setDraft(String(page))
                } else {
                  flushCommitPage()
                }
              }}
              aria-label={t('page_input_aria')}
              className={css({
                width: `${STACKED_PAGE_INPUT_WIDTH_PX}px`,
                textAlign: 'center',
                border: `1px solid ${palette.grey20}`,
                borderRadius: `${STACKED_BORDER_RADIUS_PX}px`,
                backgroundColor: palette.white,
                padding: STACKED_INPUT_PADDING,
                fontSize: `${STACKED_FONT_SIZE_PX}px`,
                color: palette.grey90,
                boxSizing: 'border-box',
              })}
            />
            <span
              className={css({
                fontSize: `${STACKED_FONT_SIZE_PX}px`,
                color: palette.grey70,
              })}
            >
              {t('of', { count: totalPages })}
            </span>
          </div>

          <Button
            kind="minimal"
            disabled={isLast}
            onClick={() => onPageChange(page + 1)}
            overrides={stackedPrevNextOverrides(isLast)}
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: `${STACKED_PREV_NEXT_GAP_PX}px`,
            })}
          >
            <span className={css({ fontWeight: 800 })}>{t('next')}</span>
            <i className="fa fa-chevron-right" aria-hidden="true" />
          </Button>
        </div>

        {/* Row 3: rows-per-page, right-aligned. */}
        <label
          className={`d-flex align-items-center justify-content-end ${css({
            fontSize: `${STACKED_FONT_SIZE_PX}px`,
            color: palette.grey70,
            gap: `${STACKED_ROWS_PER_PAGE_GAP_PX}px`,
          })}`}
        >
          <span>{t('rows_per_page_label')}</span>
          <select
            aria-label={t('rows_per_page_aria')}
            value={pageSize}
            onChange={e => {
              const parsed = Number(e.target.value)
              const next = PAGE_SIZE_OPTIONS.find(size => size === parsed)
              if (next) onPageSizeChange(next)
            }}
            className={css({
              padding: STACKED_SELECT_PADDING,
              backgroundColor: palette.white,
              border: `1px solid ${palette.grey20}`,
              borderRadius: `${STACKED_BORDER_RADIUS_PX}px`,
              color: palette.grey90,
              fontSize: `${STACKED_FONT_SIZE_PX}px`,
              cursor: 'pointer',
              appearance: 'none',
              // Inline caret glyph, reused from the row layout's select below
              // (fill built off the palette token; `%23` is the URL-encoded `#`).
              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='${palette.grey70.replace(
                '#',
                '%23'
              )}' d='M0 0l5 6 5-6z'/></svg>")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 7px center',
            })}
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
    )
  }

  return (
    <div
      className={`d-flex align-items-center justify-content-between ${css({
        padding: '24px 0 16px',
        flexWrap: 'wrap',
      })}`}
    >
      <span
        className={css({
          fontSize: '14px',
          color: palette.grey70,
          flex: '1 0 0',
        })}
      >
        {t('showing_results', { start, end, count: totalCount })}
      </span>

      <div
        className={`d-flex align-items-center ${css({
          gap: `${PAGINATION_GROUP_GAP_PX}px`,
        })}`}
      >
        <Button
          kind="minimal"
          disabled={isFirst}
          onClick={() => onPageChange(page - 1)}
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: `${PREV_NEXT_GAP_PX}px`,
          })}
        >
          <i className="fa fa-chevron-left" aria-hidden="true" />
          <span
            className={css({
              fontWeight: 'bold',
              fontSize: '16px',
              color: isFirst ? palette.grey40 : palette.grey90,
            })}
          >
            {t('prev')}
          </span>
        </Button>

        <div
          className={`d-flex align-items-center ${css({
            gap: `${PAGE_GROUP_GAP_PX}px`,
          })}`}
        >
          <span className={css({ fontSize: '14px', color: palette.grey90 })}>
            {t('page')}
          </span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={draft}
            onChange={e => handleDraftChange(e.target.value)}
            onKeyDown={e => {
              // Enter commits immediately rather than waiting out the debounce.
              if (e.key === 'Enter') flushCommitPage()
            }}
            onBlur={() => {
              // A valid draft still has a pending debounced commit at blur time;
              // flush it so navigating away doesn't discard it. An invalid/empty
              // draft has nothing worth committing, so cancel and snap back.
              if (isNaN(parseInt(draft, 10))) {
                cancelCommitPage()
                setDraft(String(page))
              } else {
                flushCommitPage()
              }
            }}
            aria-label={t('page_input_aria')}
            className={css({
              width: `${PAGE_INPUT_WIDTH_PX}px`,
              height: `${PAGE_INPUT_HEIGHT_PX}px`,
              textAlign: 'center',
              border: `1px solid ${palette.grey40}`,
              borderRadius: '4px',
              backgroundColor: merchantTheme.colors.grey05,
              padding: '11px 16px',
              fontSize: '16px',
              lineHeight: '24px',
              boxSizing: 'border-box',
            })}
          />
          <span className={css({ fontSize: '14px', color: palette.grey90 })}>
            {t('of', { count: totalPages })}
          </span>
        </div>

        <Button
          kind="minimal"
          disabled={isLast}
          onClick={() => onPageChange(page + 1)}
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: `${PREV_NEXT_GAP_PX}px`,
          })}
        >
          <span
            className={css({
              fontWeight: 'bold',
              fontSize: '16px',
              color: isLast ? palette.grey40 : palette.grey90,
            })}
          >
            {t('next')}
          </span>
          <i className="fa fa-chevron-right" aria-hidden="true" />
        </Button>
      </div>

      <label
        className={`d-flex align-items-center justify-content-end ${css({
          fontSize: '14px',
          color: palette.grey70,
          flex: '1 0 0',
          gap: `${ROWS_PER_PAGE_GAP_PX}px`,
        })}`}
      >
        <span>{t('rows_per_page_label')}</span>
        <select
          aria-label={t('rows_per_page_aria')}
          value={pageSize}
          onChange={e => {
            const parsed = Number(e.target.value)
            const next = PAGE_SIZE_OPTIONS.find(size => size === parsed)
            if (next) onPageSizeChange(next)
          }}
          className={css({
            width: `${SELECT_WIDTH_PX}px`,
            height: `${SELECT_HEIGHT_PX}px`,
            padding: '0 28px 0 12px',
            backgroundColor: palette.white,
            border: `1px solid ${palette.grey40}`,
            borderRadius: '4px',
            color: palette.grey70,
            fontSize: '16px',
            cursor: 'pointer',
            appearance: 'none',
            // Inline caret glyph. The fill is grey70, built off the palette token
            // (a data URI cannot reference a JS constant, so `%23` is the URL-encoded
            // `#`) rather than a bare hardcoded hex.
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='${palette.grey70.replace(
              '#',
              '%23'
            )}' d='M0 0l5 6 5-6z'/></svg>")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 10px center',
          })}
        >
          {PAGE_SIZE_OPTIONS.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}

export default TablePaginationFooter
